import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { User } from '@/types/user';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import {
  NaturalPerson,
  NaturalPersonDomicileAddress,
  NaturalPersonPhoneNumber,
  NaturalPersonURI
} from '@/types/naturalPerson';
import { createResourceUri } from '@/utils/links';
import { VirtualIdentity, VirtualIdentityType } from '@/types/virtualIdentities';
import { PartnershipURI } from '@/types/partnership';
import { PartnershipDeclarationList, PartnershipDeclarationStatus } from '@/types/partnershipDeclaration';

export const useUserStore = defineStore('user', {
  state: (): User => ({
    uid: '',
    birthdate: '',
    created: '',
    domicile: '',
    email: '',
    familyName: '',
    gender: '',
    identificationOnly: false,
    isRegistered: false,
    name: '',
    nationality: '',
    status: '',
    address: undefined,
    phone: undefined,
    virtualIdentities: undefined,
    partnershipDeclarations: undefined
  }),
  getters: {
    getInitials(state): string {
      return `${state.name.slice(0, 1)}${state.familyName.slice(0, 1)}`;
    },
    getFullName(state): string {
      return `${state.name} ${state.familyName}`;
    },
    isPotatoOnly(state): boolean {
      return state.isRegistered;
    },
    isLimitedUser(state): boolean {
      return this.isPotatoOnly || state.identificationOnly;
    },
    isIdentOnlyQES(state): boolean {
      const partnershipsStore = usePartnershipsStore();
      return state.identificationOnly && !partnershipsStore.hasPartnerships;
    },
    hasEbillActivated(state): boolean {
      return (
        !!state.virtualIdentities && state.virtualIdentities.some((vi) => vi.typeName === VirtualIdentityType.Ebill)
      );
    },
    hasPartnershipDeclarations(state): boolean {
      return !!state.partnershipDeclarations && state.partnershipDeclarations.length > 0;
    }
  },
  actions: {
    async setUser(user: NaturalPerson) {
      this.uid = user.uid;
      this.birthdate = user.detailed.dateOfBirth?.slice(0, 10) || '';
      this.created = user.relationshipCreationDate;
      this.domicile = user.contactDetails.domicileAddress?.country || '';
      this.email = user.contactDetails.emailAddress;
      this.familyName = user.base.names?.join(' ') || '';
      this.gender = user.base.gender || '';
      this.identificationOnly = user.base.identificationOnly || false;
      this.isRegistered = user.base.isRegistered || false;
      this.name = user.base.givenNames?.join(' ') || '';
      this.nationality = user.detailed.nationalities?.join(' ') || '';
      this.status = user.status;
      await Promise.all([
        this.updateAddress(),
        this.updatePhoneNumber(),
        this.updateVirtualIdentities(),
        this.updatePartnershipDeclarations()
      ]);
    },
    async refreshUser() {
      const userData = await apiGw.get(
        createResourceUri(NaturalPersonURI.Me, { queryParams: { contactDetails: true } })
      );
      await this.setUser(userData.data);
    },
    setAddressValidUntil(date: string | undefined) {
      if (this.address?.planned?.validFrom) {
        this.address.planned.validFrom = date || '';
      }
    },
    async updateAddress() {
      const res = await apiGw.get(
        createResourceUri(NaturalPersonURI.BaseV2, { uid: this.uid, resource: NaturalPersonURI.Domicile })
      );
      this.address = res.data as NaturalPersonDomicileAddress;
    },
    async updatePhoneNumber() {
      const res = await apiGw.get(
        createResourceUri(NaturalPersonURI.BaseV2, { uid: this.uid, resource: NaturalPersonURI.PhoneNumber })
      );
      this.phone = res.data as NaturalPersonPhoneNumber;
    },
    async updateVirtualIdentities() {
      const res = await apiGw.get(
        createResourceUri(NaturalPersonURI.BaseV2, { uid: this.uid, resource: NaturalPersonURI.VirtualIdentities })
      );
      this.virtualIdentities = res.data as VirtualIdentity[];
    },
    async updatePartnershipDeclarations() {
      const res = await apiGw.post(createResourceUri(`${PartnershipURI.Base}${PartnershipURI.DeclarationsList}`));
      if (!res.data || !Array.isArray(res.data)) {
        this.partnershipDeclarations = undefined;
      } else {
        this.partnershipDeclarations = res.data.filter(
          (declaration: PartnershipDeclarationList) => declaration.status !== PartnershipDeclarationStatus.Cancelled
        ) as PartnershipDeclarationList[];
      }
    }
  }
});
